<script setup lang="ts">
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { inject, nextTick, reactive, ref, watch } from 'vue';
import { getBetterUrl } from '@/utils/uri-utils';
import { canUseDOM } from '@/utils/dom-utils';
import { debounce } from 'lodash-es';
import type { SiteRichTextProps, SiteRichTextRef } from '../types/elements/site-rich-text';
import useDevice from '@/hooks/useDevice';
import gsap from 'gsap';
import utils from '@/utils';
import EventBus from '@/utils/bus';
import BUS_EVENTS from '@/utils/bus-events';

const props = withDefaults(defineProps<SiteRichTextProps>(), {
  hasLinkAnimation: true,
  tag: 'div',
});
const $bus = inject<EventBus>('$bus')!;
const { isMobileOrTablet } = useDevice();
const state = reactive({
  richField: props.field,
  eventBound: false,
});
const rootEl = ref<SiteRichTextRef>();

const getTooltipMsg = (e: MouseEvent) => {
  const $el = e.target as HTMLElement;
  const $tip = $el.closest('span[data-tooltip]') as HTMLElement | undefined;
  return $tip?.dataset.tooltip;
};
const onMouseEnter = debounce((e: MouseEvent) => {
  if (isMobileOrTablet.value) return;
  const el = e.target as HTMLElement;
  const target = el.querySelector('em') as HTMLElement | undefined;
  $bus.emit(BUS_EVENTS.TOOLTIP_SHOW, {
    text: getTooltipMsg(e),
    target: target,
  });
}, 100);
const onMouseLeave = debounce((e: MouseEvent) => {
  if (isMobileOrTablet.value) return;
  $bus.emit(BUS_EVENTS.TOOLTIP_HIDE);
}, 100);
const onClick = debounce((e: MouseEvent) => {
  if (isMobileOrTablet.value) {
    $bus.emit(BUS_EVENTS.TOOLTIP_SHOW, {
      text: getTooltipMsg(e),
      target: e.target,
    });
  }
}, 200);
const update = debounce(async () => {
  const $container = rootEl.value?.$el;
  if (!canUseDOM() || !props.field || props.editable) {
    state.richField = props.field;
    return;
  }
  if (state.eventBound && $container && typeof $container.querySelectorAll === 'function') {
    const $tooltips = $container.querySelectorAll('span[data-tooltip]') as NodeListOf<HTMLElement>;
    console.log($tooltips);
    $tooltips.forEach((tp) => {
      tp.removeEventListener('mouseenter', onMouseEnter);
      tp.removeEventListener('mouseleave', onMouseLeave);
    });
    state.eventBound = false;
  }
  const { value, ...rest } = props.field;
  const div = document.createElement('DIV');
  div.innerHTML = value ?? '';
  const links = div.getElementsByTagName('A') as HTMLCollectionOf<HTMLAnchorElement>;
  for (const link of links) {
    link.href = getBetterUrl(link.href);
  }
  state.richField = {
    value: div.innerHTML,
    ...rest,
  };
  div.remove();
  await nextTick();
  if ($container && typeof $container.querySelectorAll === 'function') {
    const $tooltips = $container.querySelectorAll('span[data-tooltip]') as NodeListOf<HTMLElement>;
    $tooltips.forEach((tp) => {
      tp.addEventListener('mouseenter', onMouseEnter);
      tp.addEventListener('mouseleave', onMouseLeave);
    });
    state.eventBound = true;
  }
}, 100);
watch(() => props.field?.value, update, { immediate: true });
</script>
<template>
  <rich-text
    :tag="tag"
    :editable="editable"
    :field="state.richField"
    class="e-rich-text"
    :class="[{ 'has-link-animation': hasLinkAnimation }]"
    @click="onClick"
    v-if="state.richField"
    ref="rootEl"
  />
</template>

<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

$link-animation-duration: 600ms;
$link-animation-easing: cubic-bezier(0.355, 0.005, 0.26, 1);
$link-animation-transition: all $link-animation-duration $link-animation-easing;

@mixin rich-text-animated-link {
  display: inline-block;
  position: relative;
  text-decoration: underline;
  box-shadow: inset 0 0 0 0 $white;
  transition: $link-animation-transition;
  outline: none;
}

.e-rich-text {
  &.has-link-animation a[href] {
    @include rich-text-animated-link;
  }
  span[data-tooltip] {
    position: relative;
    cursor: pointer;
    > em {
      margin-left: 4px;
    }
  }

  :is(h1, h2, h3, h4, h5, h6) {
    margin: 0;
  }
}
</style>
